// ===== Planilhas de importação (T1.15): leitura do CSV e das células, sem I/O =====
import { parseScaled } from "./decimal";
import { DomainError } from "./errors";
import { isIsoDate } from "./dates";

/** Tetos de uma planilha de importação (RF-PRF-09). */
export const MAX_IMPORT_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_IMPORT_ROWS = 10_000;
export const MAX_IMPORT_COLUMNS = 100;
export const MAX_IMPORT_CELL_CHARS = 10_000;
/**
 * Células preenchidas por planilha XLSX (revisão de segurança da T1.15): 10.000 linhas × 100 colunas = 1 milhão de
 * células cabem num XLSX de 85 KB; o teto segura a memória (o maior modelo, contatos, tem 23 colunas = 230 mil). O CSV
 * não precisa: o arquivo de 10 MB já limita as células, e 1 milhão de células curtas medem ~50 MB de heap.
 */
export const MAX_IMPORT_CELLS = 300_000;

/**
 * Célula lida: texto (CSV e texto do XLSX) ou número do XLSX como está gravado no arquivo ("1234.5", "1E-3"), para
 * converter sem passar por float. O número é um texto primitivo marcado com NUL no início (`numCell`/`cellNum`), não
 * um objeto: 300 mil células numéricas não viram 300 mil objetos. Texto lido nunca começa com NUL (o leitor tira).
 */
export type SheetCell = string;
export type SheetLimits = { maxRows: number; maxColumns: number; maxCellChars: number; maxCells: number };
export type CellResult<T> = { ok: true; value: T } | { ok: false; error: string };

const NUM_TAG = "\u0000";
/** Célula numérica do XLSX (o texto do número como está no arquivo). */
export const numCell = (num: string): SheetCell => NUM_TAG + num;
/** O texto do número se a célula for numérica do XLSX; null para texto ou vazio. */
export const cellNum = (c: SheetCell | undefined): string | null =>
  c !== undefined && c.charCodeAt(0) === 0 ? c.slice(1) : null;
/** Texto lido de um arquivo: sem NUL no início (senão seria confundido com um número do XLSX). */
export const plainText = (s: string): string => (s.charCodeAt(0) === 0 ? s.replace(/^\0+/, "") : s);

const LIMITS: SheetLimits = {
  maxRows: MAX_IMPORT_ROWS,
  maxColumns: MAX_IMPORT_COLUMNS,
  maxCellChars: MAX_IMPORT_CELL_CHARS,
  maxCells: Number.POSITIVE_INFINITY,
};
const fileError = (msg: string): never => {
  throw new DomainError("invalid_file", msg);
};
const fmt = (n: number) => n.toLocaleString("pt-BR");
/** Mensagem do teto de células. */
export const tooManyCellsMessage = (max: number) =>
  `A planilha tem células demais (mais de ${fmt(max)} preenchidas). Divida o arquivo ou envie em CSV.`;

/** 0x80–0x9F do Windows-1252; o resto é Latin-1 (os 5 bytes sem caractere viram o controle C1, como no WHATWG). */
const CP1252 = "€\u0081‚ƒ„…†‡ˆ‰Š‹Œ\u008DŽ\u008F" + "\u0090‘’“”•–—˜™š›œ\u009DžŸ";
const CP1252_HIGH = Uint16Array.from(CP1252, (ch) => ch.charCodeAt(0));
const DECODE_CHUNK = 8192;

/**
 * Windows-1252 em pedaços de 8 K: cada pedaço é mapeado num Uint16Array e vira uma string de uma vez; no fim, um join.
 * (Uma string por byte, `text += ...`, retinha centenas de MB para 10 MB de arquivo.)
 */
function decodeWindows1252(bytes: Uint8Array): string {
  const buf = new Uint16Array(DECODE_CHUNK);
  const parts: string[] = [];
  for (let off = 0; off < bytes.length; off += DECODE_CHUNK) {
    const n = Math.min(DECODE_CHUNK, bytes.length - off);
    for (let k = 0; k < n; k++) {
      const b = bytes[off + k]!;
      buf[k] = b >= 0x80 && b <= 0x9f ? CP1252_HIGH[b - 0x80]! : b;
    }
    parts.push(String.fromCharCode.apply(null, buf.subarray(0, n) as unknown as number[]));
  }
  return parts.join("");
}

/** UTF-8 (com ou sem BOM); sem UTF-8 válido, Windows-1252 (o "CSV" do Excel em português). */
export function decodeSpreadsheetText(bytes: Uint8Array): {
  text: string;
  encoding: "utf-8" | "windows-1252";
} {
  try {
    return {
      text: new TextDecoder("utf-8", { fatal: true, ignoreBOM: false }).decode(bytes),
      encoding: "utf-8",
    };
  } catch {
    return { text: decodeWindows1252(bytes), encoding: "windows-1252" };
  }
}

const DELIMITERS = [";", "\t", ","] as const;
const QUOTE = 0x22;
const LF = 0x0a;
const CR = 0x0d;
export type CsvDelimiter = (typeof DELIMITERS)[number];

/** O separador mais frequente fora de aspas na primeira linha; empate (ou nenhum) fica com ";". */
export function detectCsvDelimiter(text: string): CsvDelimiter {
  const counts = new Map<string, number>(DELIMITERS.map((d) => [d, 0]));
  let quoted = false;
  for (const ch of text) {
    if (ch === '"') quoted = !quoted;
    else if (!quoted && (ch === "\n" || ch === "\r")) break;
    else if (!quoted && counts.has(ch)) counts.set(ch, counts.get(ch)! + 1);
  }
  let best: CsvDelimiter = ";";
  for (const d of DELIMITERS) if (counts.get(d)! > counts.get(best)!) best = d;
  return best;
}

/**
 * CSV (RFC 4180 tolerante): aspas com "" dentro, quebras de linha CRLF/LF/CR, separador detectado. Devolve todos os
 * registros, inclusive os vazios (`[""]`), para que o número da linha na planilha seja o índice + 1. Tetos: linhas não
 * vazias (além do cabeçalho), colunas, caracteres por célula e, se pedido (`maxCells`), células preenchidas.
 *
 * Memória (revisão de segurança): cada célula é um pedaço do texto (`slice`), nunca montada caractere a caractere; a
 * célula entre aspas junta os trechos entre as aspas duplas.
 */
export function parseCsv(
  input: string,
  o: Partial<SheetLimits> & { delimiter?: CsvDelimiter } = {},
): string[][] {
  const lim = { ...LIMITS, ...o };
  const text = input.startsWith("\uFEFF") ? input.slice(1) : input;
  const n = text.length;
  const d = (o.delimiter ?? detectCsvDelimiter(text)).charCodeAt(0);
  const rows: string[][] = [];
  let row: string[] = [];
  let parts: string[] = [];
  let cellLen = 0;
  let filled = 0;
  let nonEmpty = 0;
  const tooLong = () =>
    fileError(`Célula com mais de ${fmt(lim.maxCellChars)} caracteres na linha ${rows.length + 1}.`);
  const add = (s: string) => {
    if (s === "") return;
    parts.push(s);
    cellLen += s.length;
    if (cellLen > lim.maxCellChars) tooLong();
  };
  const pushCell = () => {
    const cell = plainText(parts.length === 1 ? parts[0]! : parts.join(""));
    parts = [];
    cellLen = 0;
    if (cell !== "" && ++filled > lim.maxCells) fileError(tooManyCellsMessage(lim.maxCells));
    row.push(cell);
    if (row.length > lim.maxColumns) fileError(`A planilha tem mais de ${fmt(lim.maxColumns)} colunas.`);
  };
  const pushRow = () => {
    pushCell();
    if (row.some((c) => c.trim() !== "") && ++nonEmpty > lim.maxRows + 1)
      fileError(`A planilha tem mais de ${fmt(lim.maxRows)} linhas. Divida o arquivo.`);
    rows.push(row);
    row = [];
  };
  let i = 0;
  // Início do trecho sem aspas da célula atual (vai até o separador, a quebra de linha ou o fim).
  let run = 0;
  while (i < n) {
    const ch = text.charCodeAt(i);
    if (ch === QUOTE && cellLen === 0 && i === run) {
      // Entre aspas: trechos até cada aspa; "" vira uma aspa. Teto também aqui: aspa sem fechar não acumula o arquivo.
      const quoteLine = rows.length + 1;
      i++;
      for (;;) {
        const q = text.indexOf('"', i);
        if (q < 0) {
          if (cellLen + (n - i) > lim.maxCellChars) tooLong();
          fileError(`Aspas sem fechar a partir da linha ${quoteLine}.`);
        }
        add(text.slice(i, q));
        if (text.charCodeAt(q + 1) !== QUOTE) {
          i = q + 1;
          break;
        }
        add('"');
        i = q + 2;
      }
      run = i;
      continue;
    }
    if (ch === d) {
      add(text.slice(run, i));
      pushCell();
      run = ++i;
    } else if (ch === LF || ch === CR) {
      add(text.slice(run, i));
      pushRow();
      i += ch === CR && text.charCodeAt(i + 1) === LF ? 2 : 1;
      run = i;
    } else i++;
  }
  add(text.slice(run, n));
  if (cellLen > 0 || row.length > 0) pushRow();
  return rows;
}

/** Linha sem nenhum valor (pulada na importação, mas conta na numeração). */
export const isBlankRow = (row: readonly (SheetCell | undefined)[]): boolean =>
  row.every((c) => cellText(c) === "");

/** Nome de coluna comparável: sem acento, caixa, pontuação e espaços repetidos ("Preço (R$)" → "preco r"). */
export function normalizeHeader(s: string): string {
  return s
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

const NUMBER = /^([+-]?)(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/;

/** Número do XLSX ("1.5E3", "1E-3") como decimal sem expoente ("1500", "0.001"), sem passar por float. */
export function plainDecimal(num: string): string {
  const m = NUMBER.exec(num.trim());
  if (!m || (m[2] === "" && (m[3] ?? "") === "")) throw new DomainError("invalid_number", "Número inválido");
  const exp = Number(m[4] ?? "0");
  if (Math.abs(exp) > 30) throw new DomainError("invalid_number", "Valor acima do limite");
  // O grupo 2 (\d*) sempre casa, mesmo vazio.
  let digits = m[2]! + (m[3] ?? "");
  let point = m[2]!.length + exp;
  if (point < 0) {
    digits = "0".repeat(-point) + digits;
    point = 0;
  }
  if (point > digits.length) digits = digits.padEnd(point, "0");
  const int = digits.slice(0, point).replace(/^0+(?=\d)/, "") || "0";
  const frac = digits.slice(point).replace(/0+$/, "");
  return `${m[1] === "-" ? "-" : ""}${int}${frac ? `.${frac}` : ""}`;
}

/** Decimal com ponto ("0.30000000000000004") arredondado meio para cima (longe do zero) em `scale` casas. */
export function roundDecimalString(s: string, scale: number): string {
  const negative = s.startsWith("-");
  const [int = "0", frac = ""] = (negative ? s.slice(1) : s).split(".");
  const scaled = BigInt(int + frac.padEnd(scale, "0").slice(0, scale));
  const up = (frac[scale] ?? "0") >= "5" ? 1n : 0n;
  const v = (scaled + up).toString().padStart(scale + 1, "0");
  const out = scale ? `${v.slice(0, -scale)}.${v.slice(-scale)}` : v;
  return negative && /[1-9]/.test(out) ? `-${out}` : out;
}

/** Texto da célula sem espaços nas pontas; número do XLSX sem notação científica. */
export function cellText(c: SheetCell | undefined): string {
  if (c === undefined) return "";
  const num = cellNum(c);
  if (num === null) return c.trim();
  try {
    return plainDecimal(num);
  } catch {
    return num.trim();
  }
}

/**
 * Número escalado (centavos = 2, q4/pct4 = 4) ou null (vazio). Texto segue a regra pt-BR do formulário (`parseScaled`:
 * vírgula decimal, ponto de milhar, "R$" e "%" ignorados; mais casas que a escala é erro); número do XLSX é arredondado
 * meio para cima (a planilha mostra o valor arredondado).
 */
export function parseDecimalCell(
  c: SheetCell | undefined,
  scale: number,
  o: { max?: number; allowNegative?: boolean } = {},
): CellResult<number | null> {
  const max = o.max ?? Number.MAX_SAFE_INTEGER;
  let value: number;
  const num = cellNum(c);
  if (num !== null) {
    if (num.trim() === "") return { ok: true, value: null };
    let plain: string;
    try {
      plain = plainDecimal(num);
    } catch (e) {
      return { ok: false, error: (e as Error).message };
    }
    const n = BigInt(roundDecimalString(plain, scale).replace(".", ""));
    if (n > BigInt(max) || -n > BigInt(max)) return { ok: false, error: "Valor acima do limite" };
    value = Number(n);
  } else {
    const t = cellText(c);
    if (t === "") return { ok: true, value: null };
    try {
      value = parseScaled(t, scale, "invalid_number");
    } catch (e) {
      const m = (e as Error).message;
      if (m.includes("casas")) return { ok: false, error: `Use no máximo ${scale} casas decimais` };
      if (m.includes("limite")) return { ok: false, error: "Valor acima do limite" };
      return { ok: false, error: "Número inválido" };
    }
    if (Math.abs(value) > max) return { ok: false, error: "Valor acima do limite" };
  }
  if (value < 0 && !o.allowNegative) return { ok: false, error: "Valor não pode ser negativo" };
  return { ok: true, value };
}

const DAY_MS = 86_400_000;
/** Maior número de série do Excel (31/12/9999). */
const MAX_SERIAL = 2_958_465;

/**
 * Número de série de data do Excel (parte inteira; a fração é a hora). Sistema 1900: o Excel conta o 29/02/1900, que
 * não existe (série 60 = null); sistema 1904 (Mac antigo): série 0 = 01/01/1904.
 */
export function excelSerialToIso(serial: number, date1904 = false): string | null {
  const s = Math.floor(serial);
  if (!Number.isFinite(s) || s > MAX_SERIAL || s < (date1904 ? 0 : 1) || (!date1904 && s === 60)) return null;
  const base = date1904 ? Date.UTC(1904, 0, 1) : s < 60 ? Date.UTC(1899, 11, 31) : Date.UTC(1899, 11, 30);
  return new Date(base + s * DAY_MS).toISOString().slice(0, 10);
}

const BR_DATE = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})(?:\s+\d{1,2}:\d{2}(?::\d{2})?)?$/;
const ISO_DAY = /^(\d{4}-\d{2}-\d{2})(?:[T\s]\d{1,2}:\d{2}(?::\d{2})?)?$/;
const BAD_DATE = "Data inválida (use dd/mm/aaaa)";

/** dd/mm/aaaa (também com - ou .), aaaa-mm-dd, ou número de série da célula de data do XLSX; vazio = null. */
export function parseDateCell(
  c: SheetCell | undefined,
  o: { date1904?: boolean } = {},
): CellResult<string | null> {
  const num = cellNum(c);
  if (num !== null) {
    if (num.trim() === "") return { ok: true, value: null };
    const iso = excelSerialToIso(Number(num), o.date1904);
    return iso ? { ok: true, value: iso } : { ok: false, error: BAD_DATE };
  }
  const t = cellText(c);
  if (t === "") return { ok: true, value: null };
  const br = BR_DATE.exec(t);
  const iso = br ? `${br[3]}-${br[2]!.padStart(2, "0")}-${br[1]!.padStart(2, "0")}` : ISO_DAY.exec(t)?.[1];
  return iso && isIsoDate(iso) ? { ok: true, value: iso } : { ok: false, error: BAD_DATE };
}

const TRUE = new Set(["sim", "s", "x", "1", "verdadeiro", "true", "yes", "y"]);
const FALSE = new Set(["nao", "n", "0", "falso", "false", "no"]);

/** Sim/Não (também S/N, X, 1/0, Verdadeiro/Falso); vazio = null. */
export function parseBoolCell(c: SheetCell | undefined): CellResult<boolean | null> {
  const t = normalizeHeader(cellText(c));
  if (t === "") return { ok: true, value: null };
  if (TRUE.has(t)) return { ok: true, value: true };
  if (FALSE.has(t)) return { ok: true, value: false };
  return { ok: false, error: "Use Sim ou Não" };
}
